/**
 * Chat-tool registry — the SINGLE source of truth for the composer's Tools
 * menu. Each tool is one self-describing descriptor: display metadata, the
 * knowledge contexts it's offered in, its deployment gate, the role permission
 * (RBAC) it needs per knowledge type, and the wire-payload flags it contributes
 * while active.
 *
 * Canvas and Search plus were removed here (2026-08): the backend no longer supports
 * either, and this registry being the single source of truth meant withdrawing them
 * from every surface was deleting two descriptors — no component changed.
 */

import type { ComponentType, SVGProps } from 'react';
import { ReactComponent as WebSearchIcon } from 'src/assets/tailwind/chat/globe.svg';
import { ReactComponent as ImageGenIcon } from 'src/assets/tailwind/chat/image-icon.svg';
import { ReactComponent as CompareIcon } from 'src/assets/tailwind/chat/split-view.svg';
import { IS_WEB_SEARCH_ENABLED } from 'src/config';
import type { SendMessagePayload } from 'src/content/ChatV2/commands/SendMessageCommand';
import { COMPARE_CHAT_PATH } from 'src/content/ChatV2/constants';
import {
  CHAT_TOOL,
  KNOWLEDGE_TYPE,
  PLUS_MENU_SUBMENU,
  TOOL_SELECTION
} from 'src/content/ChatV2/enums';
import {
  type FeaturePermissionSpec,
  type IsGranted,
  isFeatureAllowed
} from 'src/content/ChatV2/lib/permissions/chatFeatureAccess';
import { FEATURE_PERMISSIONS } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export interface ChatToolDescriptor {
  id: CHAT_TOOL;
  title: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  knowledge?: readonly KNOWLEDGE_TYPE[];
  isEnabled?: () => boolean;
  permission?: FeaturePermissionSpec;
  payloadFlags: Partial<SendMessagePayload>;
  selection?: TOOL_SELECTION;
  navigateTo?: string;
  submenu?: PLUS_MENU_SUBMENU;
}

/** Canonical order = menu order. */
export const CHAT_TOOL_REGISTRY: readonly ChatToolDescriptor[] = [
  {
    id: CHAT_TOOL.WEB_SEARCH,
    title: T.webSearch,
    description: T.webSearchDescription,
    icon: WebSearchIcon,
    knowledge: [KNOWLEDGE_TYPE.WORLD],
    isEnabled: () => IS_WEB_SEARCH_ENABLED,
    permission: {
      [KNOWLEDGE_TYPE.WORLD]: FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE_WEB_SEARCH,
      [KNOWLEDGE_TYPE.ENTERPRISE]:
        FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE_WEB_SEARCH
    },
    payloadFlags: { webSearchEnabled: true }
  },
  {
    id: CHAT_TOOL.COMPARE_MODELS,
    title: T.compareModelTitle,
    description: T.compareModelDescription,
    icon: CompareIcon,
    permission: {
      [KNOWLEDGE_TYPE.WORLD]:
        FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE_MODEL_COMPARISON,
      [KNOWLEDGE_TYPE.ENTERPRISE]:
        FEATURE_PERMISSIONS.CHAT_ENTERPRISE_KNOWLEDGE_MODEL_COMPARISON
    },
    payloadFlags: {},
    navigateTo: `/${COMPARE_CHAT_PATH}`
  },
  {
    id: CHAT_TOOL.IMAGE_GENERATION,
    title: T.imageGeneration,
    description: T.imageGenerationDescription,
    icon: ImageGenIcon,
    permission: {
      [KNOWLEDGE_TYPE.WORLD]:
        FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE_IMAGE_GENERATION,
      [KNOWLEDGE_TYPE.ENTERPRISE]:
        FEATURE_PERMISSIONS.CHAT_ENTERPRISE_KNOWLEDGE_IMAGE_GENERATION
    },
    payloadFlags: { imageGenerationEnabled: true },
    submenu: PLUS_MENU_SUBMENU.IMAGE_GENERATION
  }
];

/**
 * Turn context the availability rules are evaluated against. A mode's `allowlist`
 * is authoritative: a tool it names is offered in any knowledge type (an assistant
 * runs web search alongside enterprise knowledge), and a tool it omits is never
 * offered. Without an allowlist, each tool's `knowledge` decides. `isGranted`
 * applies each tool's role `permission`; omitted, nothing is gated by role.
 */
export interface ToolAvailabilityContext {
  knowledge: KNOWLEDGE_TYPE;
  allowlist?: readonly CHAT_TOOL[];
  isGranted?: IsGranted;
}

/**
 * Pure predicate: a tool is available when it passes EVERY declared constraint.
 * No tool-specific branches — a new kind of constraint becomes a new descriptor
 * field checked here once, not an `if` in the Composer.
 */
export function isToolAvailable(
  tool: ChatToolDescriptor,
  ctx: ToolAvailabilityContext
): boolean {
  if (ctx.allowlist && !ctx.allowlist.includes(tool.id)) {
    return false;
  }
  if (
    !ctx.allowlist &&
    tool.knowledge &&
    !tool.knowledge.includes(ctx.knowledge)
  ) {
    return false;
  }
  if (tool.isEnabled && !tool.isEnabled()) {
    return false;
  }
  if (
    ctx.isGranted &&
    !isFeatureAllowed(tool.permission, ctx.knowledge, ctx.isGranted)
  ) {
    return false;
  }
  return true;
}

/** Descriptors available in the given context, in canonical registry order. */
export function getAvailableTools(
  ctx: ToolAvailabilityContext
): ChatToolDescriptor[] {
  return CHAT_TOOL_REGISTRY.filter((tool) => isToolAvailable(tool, ctx));
}


function descriptorOf(tool: CHAT_TOOL): ChatToolDescriptor | undefined {
  return CHAT_TOOL_REGISTRY.find((entry) => entry.id === tool);
}

function selectionModeOf(tool: CHAT_TOOL): TOOL_SELECTION {
  return descriptorOf(tool)?.selection ?? TOOL_SELECTION.ADDITIVE;
}

const isExclusive = (tool: CHAT_TOOL): boolean =>
  selectionModeOf(tool) === TOOL_SELECTION.EXCLUSIVE;

/**
 * FE payload flags contributed by the selected tools — merged, in canonical registry
 * order so the result is deterministic regardless of the order the user selected them
 * in. `{}` for none, or for tools that are client-only modes.
 */
export function getToolsPayloadFlags(
  tools: readonly CHAT_TOOL[] | undefined
): Partial<SendMessagePayload> {
  if (!tools?.length) {
    return {};
  }
  return CHAT_TOOL_REGISTRY.filter((entry) => tools.includes(entry.id)).reduce<
    Partial<SendMessagePayload>
  >((flags, entry) => ({ ...flags, ...entry.payloadFlags }), {});
}

/**
 * Select or deselect one tool, returning the new selected SET.
 */
export function toggleToolSelection(
  active: readonly CHAT_TOOL[],
  tool: CHAT_TOOL
): CHAT_TOOL[] {
  if (active.includes(tool)) {
    return active.filter((id) => id !== tool);
  }
  if (isExclusive(tool)) {
    return [tool];
  }
  return CHAT_TOOL_REGISTRY.filter(
    (entry) =>
      entry.id === tool || (active.includes(entry.id) && !isExclusive(entry.id))
  ).map((entry) => entry.id);
}

/**
 * Make an arbitrary list of tools legal: registry order, no duplicates, no unknown ids,
 * and never an EXCLUSIVE tool beside anything else.
 */
export function normalizeToolSelection(
  tools: readonly CHAT_TOOL[]
): CHAT_TOOL[] {
  const known = CHAT_TOOL_REGISTRY.filter((entry) =>
    tools.includes(entry.id)
  ).map((entry) => entry.id);
  const exclusive = known.find(isExclusive);
  return exclusive ? [exclusive] : known;
}

/**
 * Route an ACTION tool navigates to, or `undefined` for a normal toggle tool.
 * The Composer calls this on select: a truthy result means "route there instead
 * of toggling this tool active". Single lookup — no per-tool branch in the UI.
 */
export function getToolNavigation(
  tool: CHAT_TOOL | undefined
): string | undefined {
  if (!tool) {
    return undefined;
  }
  return CHAT_TOOL_REGISTRY.find((entry) => entry.id === tool)?.navigateTo;
}
